import { css, useTheme } from '@emotion/react';
import { CheckCircle, Clock, Info, Music } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { ProjectListItem, UsageData } from '../../lib/api-client';
import { fontSizes, mq, spacing } from '../../theme/tokens';

export type StatusFilter = 'ready' | 'needs_music' | null;

interface StatsSectionProps {
  scripts: ProjectListItem[];
  usage: UsageData | null;
  activeStatusFilter?: StatusFilter;
  onStatusFilterChange?: (filter: StatusFilter) => void;
}

export function StatsSection({
  scripts,
  usage,
  activeStatusFilter = null,
  onStatusFilterChange,
}: StatsSectionProps) {
  const theme = useTheme();

  const totalScripts = scripts.length;
  const readyCount = scripts.filter((s) => s.pipelineStatus === 'ready').length;
  const needsMusicCount = scripts.filter((s) =>
    ['needs_analysis', 'needs_rehearsal', 'needs_music'].includes(s.pipelineStatus),
  ).length;

  const needsMusicSubtitle =
    needsMusicCount === 0
      ? 'All set!'
      : `${needsMusicCount} need${needsMusicCount === 1 ? 's' : ''} generation`;

  const [tooltipVisible, setTooltipVisible] = useState(false);

  const minutesUsed = usage?.generatedMinutesThisMonth ?? 0;
  const minutesMax = usage?.limits.maxMonthlyMinutes ?? null;
  const minutesDisplay = Number.isInteger(minutesUsed)
    ? minutesUsed
    : parseFloat(minutesUsed.toFixed(1));
  const usagePercent = minutesMax !== null && minutesMax > 0 ? (minutesUsed / minutesMax) * 100 : 0;
  const usageAtLimit = usagePercent >= 100;
  const usageWarning = usagePercent >= 80 && !usageAtLimit;

  const compactCardStyle = css`
    display: flex;
    align-items: center;
    gap: ${spacing.sm};
    padding: ${spacing.sm} ${spacing.md};
    border-radius: 8px;
    border: 1px solid ${theme.colors.border};
    min-width: 0;
  `;

  const clickableCompactStyle = (isActive: boolean) => css`
    ${compactCardStyle};
    cursor: pointer;
    background: ${isActive ? theme.colors.sidebarActiveBg : 'transparent'};
    border-color: ${isActive ? theme.colors.primary : theme.colors.border};
    text-align: left;
    font-family: inherit;
    transition:
      border-color 0.2s ease-in-out,
      background 0.2s ease-in-out;
    &:hover {
      border-color: ${theme.colors.primary}40;
    }
  `;

  const labelStyle = css`
    font-size: ${fontSizes.xs};
    color: ${theme.colors.textSecondary};
    white-space: nowrap;
  `;

  const numberStyle = css`
    font-size: ${fontSizes.xxl};
    font-weight: 600;
    line-height: 1;
  `;

  const subtitleStyle = css`
    font-size: ${fontSizes.xs};
    color: ${theme.colors.textMuted};
  `;

  const progressBarStyle = (percent: number, atLimit: boolean) => css`
    width: 100%;
    height: 3px;
    background: ${theme.colors.border};
    border-radius: 2px;
    overflow: hidden;
    margin-top: 2px;

    &::after {
      content: '';
      display: block;
      height: 100%;
      width: ${Math.min(percent, 100)}%;
      background: ${atLimit ? theme.colors.warning : theme.colors.primary};
      border-radius: 2px;
    }
  `;

  return (
    <div data-testid="stats-section">
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: ${spacing.sm};

          ${mq.lg} {
            flex-wrap: nowrap;
          }
        `}
      >
        {/* Total Scripts */}
        <div css={compactCardStyle}>
          <div css={numberStyle}>{totalScripts}</div>
          <div css={labelStyle}>Total Scripts</div>
        </div>

        {/* Ready to Present */}
        <button
          type="button"
          css={clickableCompactStyle(activeStatusFilter === 'ready')}
          aria-pressed={activeStatusFilter === 'ready'}
          onClick={() => onStatusFilterChange?.(activeStatusFilter === 'ready' ? null : 'ready')}
        >
          <CheckCircle
            size={16}
            aria-hidden="true"
            css={css`
              color: ${theme.colors.success};
              flex-shrink: 0;
            `}
          />
          <div css={numberStyle}>{readyCount}</div>
          <div
            css={css`
              min-width: 0;
            `}
          >
            <div css={labelStyle}>Ready to Present</div>
          </div>
        </button>

        {/* Needs Music */}
        <button
          type="button"
          css={clickableCompactStyle(activeStatusFilter === 'needs_music')}
          aria-pressed={activeStatusFilter === 'needs_music'}
          onClick={() =>
            onStatusFilterChange?.(activeStatusFilter === 'needs_music' ? null : 'needs_music')
          }
        >
          <Music
            size={16}
            aria-hidden="true"
            css={css`
              color: ${theme.colors.warning};
              flex-shrink: 0;
            `}
          />
          <div css={numberStyle}>{needsMusicCount}</div>
          <div
            css={css`
              min-width: 0;
            `}
          >
            <div css={labelStyle}>Needs Music</div>
            <div css={subtitleStyle}>{needsMusicSubtitle}</div>
          </div>
        </button>

        {/* Generated Minutes */}
        <div css={compactCardStyle}>
          <Clock
            size={16}
            aria-hidden="true"
            css={css`
              color: ${theme.colors.textSecondary};
              flex-shrink: 0;
            `}
          />
          <div
            css={css`
              ${numberStyle};
              color: ${usageAtLimit
                ? theme.colors.error
                : usageWarning
                  ? theme.colors.warning
                  : 'inherit'};
            `}
            data-testid="minutes-number"
          >
            {minutesDisplay}
          </div>
          <div
            css={css`
              min-width: 0;
              flex: 1;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.xs};
              `}
            >
              <span css={labelStyle}>Generated Minutes</span>
              <span
                css={css`
                  position: relative;
                  display: inline-flex;
                  align-items: center;
                `}
                onMouseEnter={() => setTooltipVisible(true)}
                onMouseLeave={() => setTooltipVisible(false)}
              >
                <button
                  type="button"
                  aria-label="Usage info"
                  onClick={() => setTooltipVisible(true)}
                  css={css`
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: ${theme.colors.textSecondary};
                    padding: 0;
                    display: flex;
                    align-items: center;
                    &:hover {
                      color: ${theme.colors.text};
                    }
                  `}
                >
                  <Info size={12} aria-hidden="true" />
                </button>
                {tooltipVisible && (
                  <span
                    role="tooltip"
                    css={css`
                      position: absolute;
                      bottom: calc(100% + 8px);
                      left: 50%;
                      transform: translateX(-50%);
                      background: ${theme.colors.text};
                      color: ${theme.colors.surface};
                      font-size: ${fontSizes.xs};
                      padding: ${spacing.xs} ${spacing.sm};
                      border-radius: 4px;
                      white-space: nowrap;
                      z-index: 10;
                      pointer-events: none;
                    `}
                  >
                    Monthly audio generation limit. Resets on the 1st of each month.
                  </span>
                )}
              </span>
            </div>
            <div css={subtitleStyle}>
              {minutesMax !== null ? (
                <>
                  {minutesDisplay} / {minutesMax} min
                  <div
                    css={progressBarStyle(
                      (minutesUsed / minutesMax) * 100,
                      minutesUsed >= minutesMax,
                    )}
                  />
                </>
              ) : (
                'Unlimited'
              )}
            </div>
          </div>
          {usage && usage.limits.planName === 'free' && (
            <Link
              to="/app/projects?checkout=upgrade"
              css={css`
                font-size: ${fontSizes.xs};
                font-weight: 600;
                color: ${theme.colors.primary};
                text-decoration: none;
                white-space: nowrap;
                flex-shrink: 0;

                &:hover {
                  text-decoration: underline;
                }
              `}
            >
              Upgrade plan →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
